import React, { useState, useReducer, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ChatContext } from 'context/Context';
import { arrayReducer } from 'reducers/arrayReducer';
import { fetchHistory } from 'services/elvis';
import { toast } from 'react-toastify';
import { askElvis } from 'services/elvis';
import axios from 'axios';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { ELVIS_URL } from 'helpers/urlHelper';

axios.defaults.withCredentials = true;

const ChatProvider = ({ children }) => {
  const [threads, threadsDispatch] = useReducer(arrayReducer, []);
  const [currentThread, setCurrentThread] = useState(null);
  const [textAreaInitialHeight, setTextAreaInitialHeight] = useState(32);
  const [scrollToBottom, setScrollToBottom] = useState(true);
  const [chatsLoading, setChatsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [voiceURIs, setVoiceURIs] = useState(window.speechSynthesis.getVoices());
  const [selectedVoice, setSelectedVoice] = useState(voiceURIs[0]?.name);
  const [autoPlay, setAutoPlay] = useState(false);
  const textareaElement = useRef(null);
  const [chatWindowOpen, setChatWindowOpen] = useState(false);
  const [hideSidebar, setHideSidebar] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportChat, setReportChat] = useState(null);
  const [searchQuery, setSearchQuery] = useState({
    id: 0,
    query: null
  });
  const {
    transcript,
    listening,
    finalTranscript,
    browserSupportsSpeechRecognition
  } = useSpeechRecognition();

  const handleSubmitStream = async (e) => {
    e.preventDefault();
    if (chatsLoading) return;

    if (!message.trim()) {
      toast.error('Please enter a message!');
      return;
    }

    // Capture the current message and reset the input field immediately
    const currentQuestion = message;
    setMessage('');
    setScrollToBottom(true);
    setChatsLoading(true);

    let threadId = currentThread?.id;
    let isNewThread = !threadId;

    // 1. Set up an optimistic placeholder message
    const newMessagePlaceholder = {
      question: currentQuestion,
      answer: '', // Will be populated by the stream
      conversationId: threadId || '',
      id: 0
    };

    // Safely copy threads to avoid direct state mutation
    let updatedThreads = [...threads];
    let threadIndex = -1;

    if (isNewThread) {
      const newThread = { id: '', messages: [newMessagePlaceholder] };
      updatedThreads.splice(1, 0, newThread);
      threadIndex = 1;
      setCurrentThread(newThread);
    } else {
      threadIndex = updatedThreads.findIndex(x => x.id === threadId);
      if (threadIndex !== -1) {
        // Safely append the placeholder to the existing thread
        updatedThreads[threadIndex] = {
          ...updatedThreads[threadIndex],
          messages: [...updatedThreads[threadIndex].messages, newMessagePlaceholder]
        };
      }
    }

    // Dispatch the initial UI update (shows user's question immediately)
    threadsDispatch({ type: 'SET', payload: updatedThreads });

    try {
      const res = await axios.post(ELVIS_URL + '/api/ai/ask-stream',
        JSON.stringify({
          question: currentQuestion,
          conversationId: threadId || ''
        }),
        {
          headers: { 'Content-Type': 'application/json' },
          responseType: 'stream', // Tells Axios to expect a stream
          adapter: 'fetch',       // CRITICAL: Enables true Web Streams in Axios
        }
      );

      if (res?.status != 200) throw new Error(`HTTP error! status: ${res.status}`);

      const reader = res.data.getReader();
      const decoder = new TextDecoder('utf-8');
      let done = false;
      let buffer = '';
      let streamedAnswer = '';

      // 3. Process the stream
      while (!done) {
        const { value, done: readingDone } = await reader.read();
        done = readingDone;

        if (value) {
          buffer += decoder.decode(value, { stream: true });
          const chunks = buffer.split('\n\n');

          // Keep the last incomplete piece in the buffer
          buffer = chunks.pop() || '';
          for (const chunk of chunks) {
            const jsonStr = chunk.startsWith('data: ') ? chunk.substring(6) : '\n';

            if (jsonStr.includes('[STATUS:'))
              continue;

            try {
              const parsed = jsonStr;
              if (parsed) {
                let conversationId = '';
                let messageId = '';
                let isIdInChunk = parsed.includes('conversation_id');
                let isMessageIdInChunk = parsed.includes('message_id');
                if (isIdInChunk) {
                  conversationId = parsed.replace('\n\n').split('conversation_id:')[1]?.trim() || '';
                }
                if (isMessageIdInChunk) {
                  messageId = parsed.replace('\n\n').split('message_id:')[1]?.trim() || '';
                }
                streamedAnswer += parsed;

                // Safely update the specific message within the threads array
                updatedThreads = [...updatedThreads]; // New array reference
                const updatedMessages = [...updatedThreads[threadIndex].messages]; // New messages reference
                const lastMessageIndex = updatedMessages.length - 1;

                // Assign messageId
                if (messageId != '') {
                  updatedMessages[lastMessageIndex] = {
                    ...updatedMessages[lastMessageIndex],
                    id: +messageId
                  };
                }

                if (!isIdInChunk && !isMessageIdInChunk)
                  updatedMessages[lastMessageIndex] = {
                    ...updatedMessages[lastMessageIndex],
                    answer: streamedAnswer
                  };

                // If it's a new thread, assign the official conversation ID once the server returns it
                if (isNewThread && conversationId && updatedThreads[threadIndex].id === '') {
                  updatedThreads[threadIndex].id = conversationId;
                  updatedMessages[lastMessageIndex].conversationId = conversationId;
                  setCurrentThread(updatedThreads[threadIndex]);
                  isNewThread = false; // Prevent re-assigning on every chunk
                }

                updatedThreads[threadIndex] = {
                  ...updatedThreads[threadIndex],
                  messages: updatedMessages
                };

                // Dispatch the incremental update to re-render the UI
                threadsDispatch({ type: 'SET', payload: updatedThreads });
                setScrollToBottom(true);
              }
            } catch (err) {
              console.error("Failed to parse chunk", err);
            }
          }
        }
      }
    } catch (error) {
      toast.error('An error occurred while streaming!');
      console.error("Streaming failed", error);
    } finally {
      setChatsLoading(false);
      textareaElement?.current?.focus(); // Refocus the textarea after submission
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (chatsLoading)
      return;

    if (!message) {
      toast.error('Please enter a message!')
      return;
    }

    let newMessage = {
      question: message,
      conversationId: currentThread?.id || ''
    };

    if (newMessage) {
      try {
        setChatsLoading(true);

        const res = await askElvis(newMessage);

        if (res && res.status === 200) {
          const data = res.data;

          if (data.answer) {
            if (currentThread?.id) {
              const payload = threads[threads.findIndex(x => x.id == currentThread.id)].messages.push({
                answer: data?.answer,
                conversationId: data?.conversationId,
                question: data?.question
              });
              threadsDispatch({
                type: 'SET',
                payload: payload,
              });
            } else {
              // This is a new chat, so we need to add it to the start of the threads array 
              const id = data.conversationId;
              const newThread = { id, messages: [data], createdDate: new Date().toISOString() };

              threadsDispatch({
                type: 'SET',
                payload: [newThread, ...threads],
              });
              setCurrentThread(newThread);
            }
          }
        } else {
          toast.error('Something went wrong! Please try again later.')
          console.error('Error response:', res);
        }
      } catch (err) {
        toast.error('An error occured!', err.message || '');
        console.log(err)
      } finally {
        setChatsLoading(false);
      }
    }

    setMessage('');
    setScrollToBottom(true);
  };

  const startListening = () => {
    if (!browserSupportsSpeechRecognition) {
      toast.error("Your browser doesn't support voice input.");
      return;
    }

    if (listening) {
      console.log('Stopping speech recognition');
      SpeechRecognition.stopListening();
      setAutoPlay(false);
    } else {
      console.log('Starting speech recognition');
      SpeechRecognition.startListening();
      setAutoPlay(true);
    }
  };

  useEffect(() => {
    let timeout;

    if (listening)
      setMessage(transcript);
    else if (finalTranscript && !listening) {
      timeout = setTimeout(() => {
        handleSubmitStream(new Event('submit'));
      }, 1500);
    }
    return () => clearTimeout(timeout);
  }, [transcript, finalTranscript, listening]);

  useEffect(() => {
    const updateVoices = () => setVoiceURIs(window.speechSynthesis.getVoices());
    window.speechSynthesis.onvoiceschanged = updateVoices;
    updateVoices();

    const voices = window.speechSynthesis.getVoices();
    let premiumVoice = voices.find(v => v.name.includes('Natural') && v.lang === 'en-GB');

    if (!premiumVoice)
      premiumVoice = voices.find(v => v.name.includes('Google') && v.lang === 'en-GB');

    if (!premiumVoice)
      premiumVoice = voices.find(v => v.lang === 'en-GB');

    if (premiumVoice)
      setSelectedVoice(premiumVoice);

    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  useEffect(() => {
    setChatsLoading(true);
    fetchHistory().then(res => {
      if (res) {
        // console.log('Fetched history:', res);
        // Set current thread to most recent thread in history
        setCurrentThread(res[0]);
        // Add new chat default to front of the array
        threadsDispatch({
          type: 'SET', payload: [{
            messages: [
              {
                answer: 'Hello 😊 How can I assist you with Emplas window or door product questions?',
              },
            ]
          },
          ...res]
        });
        // If no history, set current thread to a new chat with a default message
        if (res.length === 0)
          setCurrentThread(threads[0]);
      }
    }).finally(() => setChatsLoading(false));
  }, []);

  const value = {
    threads,
    threadsDispatch,
    textAreaInitialHeight,
    setTextAreaInitialHeight,
    currentThread,
    setCurrentThread,
    scrollToBottom,
    setScrollToBottom,
    chatsLoading,
    setChatsLoading,
    handleSubmit,
    handleSubmitStream,
    message,
    setMessage,
    startListening,
    listening,
    finalTranscript,
    voiceURIs,
    selectedVoice,
    setSelectedVoice,
    autoPlay,
    textareaElement,
    chatWindowOpen,
    setChatWindowOpen,
    hideSidebar,
    setHideSidebar,
    showSearchModal,
    setShowSearchModal,
    showReportModal,
    setShowReportModal,
    reportChat,
    setReportChat,
    searchQuery,
    setSearchQuery
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

ChatProvider.propTypes = { children: PropTypes.node.isRequired };

export default ChatProvider;
